import * as ngCore from '@angular/core'

import * as input from '../../../input'
import type * as models from '../../models'
import { Store } from '../../store'
import * as cell from '../cell'

import * as tools from './tools'

/** Renders prepared rows and measures visible cell content. */
@ngCore.Component({
  imports: [tools.Component, cell.renderer.Component, input.checkbox.Component],
  selector: 'atn-ds-table-rows',
  styleUrl: './component.scss',
  templateUrl: './component.html',
})
export class Component {
  /** Shared table state used by the template. */
  public readonly store = ngCore.inject(Store)

  /** Visible cell renderer hosts measured for double-click column fitting. */
  private readonly _columnContentElementsSg = ngCore.viewChildren<
    unknown,
    ngCore.ElementRef<HTMLElement>
  >('columnContent', { read: ngCore.ElementRef })

  /** Records current rendered widths, including custom renderer descendants. */
  constructor() {
    ngCore.afterRenderEffect(() => {
      // Track prepared rows so content updates retrigger unchanged DOM queries.
      this.store.rowsSg()
      // A custom renderer may wrap its value in descendants whose scrollWidth
      // is larger than the host element. Include every descendant so fitting a
      // column accounts for the actual rendered content.
      for (const content of this._columnContentElementsSg()) {
        const element = content.nativeElement
        const columnId = element.dataset.columnId
        if (!columnId) {
          continue
        }

        const contentOffsetPx = Number(element.dataset.contentOffsetPx ?? 0)
        const contentWidthsPx = [
          element.scrollWidth,
          ...Array.from(element.querySelectorAll<HTMLElement>('*')).map(
            descendant => descendant.scrollWidth,
          ),
        ]
        // Hierarchy indentation is layout space, not part of the descendant's
        // measured scroll width, so add the prepared offset once.
        this.store.updateColumnContentWidth(
          columnId,
          Math.max(...contentWidthsPx) + contentOffsetPx,
        )
      }
    })
  }

  /** Runs the cell callback without also triggering its row callback. */
  public cellClickHandler(event: Event, onClick?: () => void): void {
    if (onClick) {
      event.stopPropagation()
      onClick()
    }
  }

  /** Runs the configured row callback. */
  public rowClickHandler(onClick?: () => void): void {
    onClick?.()
  }

  /** Toggles hierarchy state without also triggering row navigation. */
  public toggleRowClickHandler(event: Event, rowId: models.Row.Id): void {
    event.stopPropagation()
    this.store.toggleRow(rowId)
  }

  /** Requests a controlled row-selection change. */
  public selectionChangeHandler(rowId: models.Row.Id, selected: boolean): void {
    this.store.changeRowSelection(rowId, selected)
  }
}
